/**
 * "What was sent" (plan 2026-09-27-003, U6): the banner for this person's
 * status, then one card per step, rendered the way the recipient read it.
 *
 * Bodies are UNTRUSTED HTML and go through `SanitizedHtml` (DOMPurify) — never
 * a raw `dangerouslySetInnerHTML`, never `contentEditable`. A blank Gong slot
 * reads "Gong runs its own step"; a later email with no subject of its own
 * reads "Re: (same thread)". Step labels come from `sentCopyStepLabels`
 * (`Email n` / the Gong step type) — no "Day n", Terra has no cadence data.
 * When the copy is not Clay's own final copy, the source caption says so.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import { Alert } from '@clay-run/glaze/alert';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { Text } from '@clay-run/glaze/text';

import { SENT_COPY_DATA_WARNING_CAPTION, dataWarningSentence } from '../../../dispatch-inspector-logic';
import type { RepStatusView } from '../../../dispatch-rep-status';
import type { PlaybooksSentCopyResponse } from '../../../schemas';
import { sentCopyStepLabels } from '../../../sent-copy-logic';
import { SanitizedHtml } from '../../SanitizedHtml';

import { DetailPanel } from './DetailPanel';

/**
 * Email-body typography. Terra override (flagged): Glaze has no rendered-email
 * preview component, so paragraphs, links and the neutral token chips
 * (`data-token-chip`, from `sent-copy-logic.ts`) are styled here with
 * semantic tokens.
 */
const BODY_CLASS =
  'text-sm leading-relaxed text-content-primary [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:text-content-action [&_a]:underline '
  + '[&_[data-token-chip]]:rounded [&_[data-token-chip]]:bg-bg-secondary [&_[data-token-chip]]:px-1 '
  + '[&_[data-token-chip]]:text-content-tertiary';

export function Banner({ banner }: { banner: NonNullable<RepStatusView['banner']> }) {
  if (banner.tone === 'red') {
    // A red advisory, not an error the app hit — no Sentry metric.
    return (
      <Alert status="error" disableErrorLogging>
        {banner.text}
      </Alert>
    );
  }
  return <Alert status={banner.tone === 'blue' ? 'info' : 'subtle'}>{banner.text}</Alert>;
}

export function SentCopyPanel({
  query,
  view,
}: {
  query: Pick<UseQueryResult<PlaybooksSentCopyResponse>, 'isLoading' | 'isError' | 'data'>;
  view: RepStatusView;
}) {
  return (
    <DetailPanel title="What was sent">
      <div className="flex flex-col gap-2.5">
        {view.banner !== null && <Banner banner={view.banner} />}
        {query.isLoading && (
          <div className="flex justify-center p-3">
            <LoadingSpinner size="sm" />
          </div>
        )}
        {query.isError && (
          <Text size="xs" variant="tertiary">
            Couldn&apos;t load what was sent. Try again in a minute.
          </Text>
        )}
        {query.data && query.data.dataWarnings.length > 0 && (
          // U0f — a warning about the record, not the copy; the message still went out.
          <div className="flex flex-col gap-1">
            <Text size="xs" variant="tertiary">
              {SENT_COPY_DATA_WARNING_CAPTION}
            </Text>
            {query.data.dataWarnings.map((defect) => (
              <Text key={defect.token} size="xs" variant="warning">
                {dataWarningSentence(defect, 'now')}
              </Text>
            ))}
          </div>
        )}
        {query.data && <Steps data={query.data} />}
      </div>
    </DetailPanel>
  );
}

function Steps({ data }: { data: PlaybooksSentCopyResponse }) {
  const labels = sentCopyStepLabels(data.steps);
  return (
    <>
      {data.steps.length === 0 && (
        <Text size="xs" variant="tertiary">
          No copy was recorded for this person.
        </Text>
      )}
      {data.steps.map((step, index) => (
        <div key={step.ordinal} className="overflow-hidden rounded-md border border-border-secondary">
          <div className="border-b border-border-secondary bg-bg-secondary px-2.5 py-1.5">
            <Text size="xs" weight="semibold">
              {labels[index]}
            </Text>
          </div>
          {step.kind === 'gong_own' ? (
            <Text size="xs" variant="tertiary" className="px-3 py-2 italic">
              Gong runs its own step
            </Text>
          ) : (
            <div className="px-3 py-2.5">
              {step.kind === 'reply' ? (
                <Text size="sm" variant="tertiary" className="mb-2">
                  Re: (same thread)
                </Text>
              ) : (
                step.subject !== null && (
                  <Text size="sm" weight="semibold" className="mb-2">
                    {step.subject}
                  </Text>
                )
              )}
              <SanitizedHtml html={step.bodyHtml} className={BODY_CLASS} />
            </div>
          )}
        </div>
      ))}
      {data.source !== 'clay_final' && (
        <Text size="xs" variant="tertiary">
          {data.note}
        </Text>
      )}
    </>
  );
}
